import React, { useState } from 'react';
import { FaTimes, FaDownload, FaEye } from 'react-icons/fa';
import dayjs from 'dayjs';

const API_URL = '/api';

const ProductViewModal = ({ open, onClose, product }) => {
  const [loadingFile, setLoadingFile] = useState(false);

  if (!open || !product) return null;

  const handleFileDownload = async () => {
    try {
      setLoadingFile(true);
      const response = await fetch(`${API_URL}/products/${product.ProductCode}/files/product_image`);
      if (!response.ok) {
        throw new Error('Failed to download file');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${product.ProductCode}_product_image`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading product image:', error);
      alert(`Failed to download product image: ${error.message}`);
    } finally {
      setLoadingFile(false);
    }
  };

  const handleFileView = async () => {
    try {
      setLoadingFile(true);
      const response = await fetch(`${API_URL}/products/${product.ProductCode}/files/product_image`);
      if (!response.ok) {
        throw new Error('Failed to view file');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing product image:', error);
      alert(`Failed to view product image: ${error.message}`);
    } finally {
      setLoadingFile(false);
    }
  };

  const renderImagePreview = () => {
    if (!product.ProductImage) return '-';

    const isLoading = loadingFile;

    return (
      <div className="flex items-center gap-2">
        <img 
          src={`${API_URL}/products/${product.ProductCode}/files/product_image`} 
          alt="Product" 
          className="w-10 h-10 object-cover rounded"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiM2NjY2NjYiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cmVjdCB4PSIzIiB5PSIzIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHJ4PSIyIiByeT0iMiI+PC9yZWN0PjxjaXJjbGUgY3g9IjguNSIgY3k9IjguNSIgcj0iMS41Ij48L2NpcmNsZT48cG9seWxpbmUgcG9pbnRzPSIyMSAxNSAxNSAxNSA5IDkgMyAxNSI+PC9wb2x5bGluZT48L3N2Zz4=';
          }}
        />
        <button
          onClick={handleFileView}
          className="p-2 text-[#2b6777] hover:text-[#52ab98] disabled:opacity-50"
          disabled={isLoading}
          title="View"
        >
          <FaEye />
        </button>
        <button
          onClick={handleFileDownload}
          className="p-2 text-[#2b6777] hover:text-[#52ab98] disabled:opacity-50"
          disabled={isLoading}
          title="Download"
        >
          <FaDownload />
        </button>
        {isLoading && <span className="text-sm text-gray-500">Loading...</span>}
      </div>
    );
  };

  const sections = [
    {
      title: 'Product Information',
      fields: [
        { label: 'Product Code', value: product.ProductCode },
        { label: 'Product Name', value: product.ProductName },
        { label: 'Category', value: product.CategoryName || '-' },
        { label: 'UOM', value: product.UOMName || '-' },
        { label: 'Status', value: product.Status },
        { label: 'Short Description', value: product.Description || '-' },
      ]
    },
    {
      title: 'Product Image',
      fields: [
        {
          label: 'Product Image',
          value: renderImagePreview()
        }
      ]
    },
    {
      title: 'System Information',
      fields: [
        { 
          label: 'Created At', 
          value: product.CreatedAt ? dayjs(product.CreatedAt).format('YYYY-MM-DD HH:mm') : '-' 
        },
        { 
          label: 'Updated At', 
          value: product.UpdatedAt ? dayjs(product.UpdatedAt).format('YYYY-MM-DD HH:mm') : '-' 
        }
      ]
    }
  ];

  return (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-2 sm:px-0">
    <div className="bg-[#E5E1DA] rounded-2xl shadow-2xl w-full max-w-4xl p-4 sm:p-8 relative overflow-y-auto max-h-[90vh]">
      
      {/* Close Button */}
      <button 
        className="absolute top-3 right-3 sm:top-4 sm:right-4 text-[#2b6777] hover:text-[#52ab98] transition"
        onClick={onClose}
      >
        <FaTimes size={18} className="sm:size-8" />
      </button>

      {/* Title */}
      <h2 className="text-lg sm:text-2xl font-bold text-[#2b6777] mb-4 sm:mb-6 text-center">
        Product Details
      </h2>

      {/* Sections */}
      <div className="space-y-4 sm:space-y-6">
        {sections.map((section, index) => (
          <div key={index} className="bg-white rounded-xl p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-bold text-[#2b6777] mb-3 sm:mb-4">
              {section.title}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              {section.fields.map((field, fieldIndex) => (
                <div key={fieldIndex} className="flex flex-col">
                  <span className="text-xs sm:text-sm font-medium text-gray-600">
                    {field.label}
                  </span>
                  <span className="text-sm sm:text-base text-gray-900 mt-1">
                    {field.label === 'Status' ? (
                      <span
                        className={`inline-block px-2 sm:px-3 py-1 rounded-full text-xs sm:text-xs font-bold shadow-sm
                          ${field.value === 'Active'
                            ? 'bg-[#52ab98] text-white'
                            : 'bg-gray-300 text-gray-500'}`}
                      >
                        {field.value}
                      </span>
                    ) : (
                      field.value
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="mt-4 sm:mt-6 flex justify-end">
        <button
          className="px-3 sm:px-4 py-2 rounded bg-[#52ab98] hover:bg-[#2b6777] text-white font-semibold shadow transition text-sm sm:text-base"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

};

export default ProductViewModal; 